import { FormEvent, useCallback, useEffect, useMemo, useState } from "react";
import { ArrowUturnLeftIcon, XMarkIcon } from "@heroicons/react/24/outline";

import CoverageMap from "../onboarding/CoverageMap";
import { CoverageDistrictState, formatGbp } from "../onboarding/types";
import LoadingWheel from "../shared/LoadingWheel";
import { ButtonInput } from "../shared/Inputs/ButtonInput";
import usePostcodePriceMutations from "../../data/usePostcodePriceMutations";
import useDefaultCRUDHandlers from "../../hooks/useDefaultCRUDHandlers";
import { IPostcodeGroup } from "../../models/PostcodeGroup";
import { IOnboardingCoverageDistrict } from "../../models/Onboarding";
import { LatLng } from "../../utils/geocode";
import classNames from "../../utils/classNames";
import {
  DistrictIndex,
  areaOf,
  compareDistrictNames,
  districtOutcode,
  districtsWithinRadius,
  geocodePostcode,
  haversineMiles,
  loadDistrictIndex,
  lookupDistrictTowns,
} from "../../utils/postcodeDistricts";

interface PostcodeCoverageMapProps {
  postcodeGroups: IPostcodeGroup[];
}

interface PendingChange {
  selected: boolean;
  price: number;
  town?: string;
}

const inputClasses =
  "block w-full rounded-md border-gray-300 text-sm text-gray-900 shadow-sm focus:border-primary-500 focus:ring-primary-500";

/**
 * Map picker for the Scrap Prices page. Shows every district of every postcode area the customer has a group
 * for: solid blue = explicit postcode price, light blue = covered by the group's base price, red = excluded
 * (inactive price row). Click a district to focus it, then toggle coverage / set a price; changes are staged and
 * written in one go via POST postcodePrice/coverage. "Expand coverage" adds districts around a postcode + radius,
 * creating groups for any new areas (their other districts are written as excluded so the group doesn't cover them).
 */
export default function PostcodeCoverageMap({ postcodeGroups }: PostcodeCoverageMapProps) {
  const { saveCoverage } = usePostcodePriceMutations();
  const { saveHandlers } = useDefaultCRUDHandlers("Coverage");

  const [index, setIndex] = useState<DistrictIndex>();
  const [indexError, setIndexError] = useState<string>();
  const [pending, setPending] = useState<Map<string, PendingChange>>(new Map());
  const [focused, setFocused] = useState<string>();
  const [defaultPrice, setDefaultPrice] = useState<number>(() => postcodeGroups[0]?.basePrice ?? 150);

  const [expandOpen, setExpandOpen] = useState(postcodeGroups.length === 0);
  const [yardPostcode, setYardPostcode] = useState("");
  const [radiusMiles, setRadiusMiles] = useState(10);
  const [yardLatLng, setYardLatLng] = useState<LatLng>();
  const [expanding, setExpanding] = useState(false);
  const [expandError, setExpandError] = useState<string>();

  useEffect(() => {
    loadDistrictIndex().then(setIndex).catch((e) => setIndexError(e.message));
  }, []);

  // ---- Baseline from saved groups ------------------------------------------------------------------------------
  const baseline = useMemo(() => {
    const map = new Map<string, CoverageDistrictState & { groupName: string; groupId: string }>();
    if (!index) return map;

    for (const group of postcodeGroups) {
      const area = group.postcode.toUpperCase();
      const info = index.areas[area];
      if (!info) continue;

      const rows = new Map(
        (group.postcodePrices ?? []).map((p) => [districtOutcode(area, p.postcode), p])
      );

      for (const [district, entry] of Object.entries(info.districts)) {
        const row = rows.get(district);
        map.set(district, {
          district,
          area,
          centre: entry.c,
          distanceMiles: yardLatLng ? haversineMiles(yardLatLng, entry.c) : 0,
          selected: row ? row.isActive : group.isActive,
          price: row ? row.price : group.basePrice,
          implicit: !row,
          groupName: group.name,
          groupId: group.id,
        });
      }
    }
    return map;
  }, [index, postcodeGroups, yardLatLng]);

  // ---- Baseline + staged changes ------------------------------------------------------------------------------
  const districts = useMemo<CoverageDistrictState[]>(() => {
    const out = new Map<string, CoverageDistrictState>();
    baseline.forEach((d, k) => out.set(k, { ...d }));

    pending.forEach((change, district) => {
      const existing = out.get(district);
      if (existing) {
        out.set(district, { ...existing, selected: change.selected, price: change.price, implicit: false, pending: true });
        return;
      }
      const area = areaOf(district);
      const entry = index?.areas[area]?.districts[district];
      if (!entry) return;
      out.set(district, {
        district,
        area,
        town: change.town,
        centre: entry.c,
        distanceMiles: yardLatLng ? haversineMiles(yardLatLng, entry.c) : 0,
        selected: change.selected,
        price: change.price,
        pending: true,
      });
    });

    return Array.from(out.values());
  }, [baseline, pending, index, yardLatLng]);

  const focusedState = focused ? districts.find((d) => d.district === focused) : undefined;
  const focusedBaseline = focused ? baseline.get(focused) : undefined;

  const pendingList = useMemo(
    () =>
      Array.from(pending.entries())
        .map(([district, change]) => ({ district, ...change }))
        .sort((a, b) => compareDistrictNames(a.district, b.district)),
    [pending]
  );
  const pendingSelected = pendingList.filter((p) => p.selected).length;

  // ---- Editing ------------------------------------------------------------------------------------------------
  const stage = useCallback(
    (district: string, change: Partial<PendingChange>) => {
      setPending((prev) => {
        const next = new Map(prev);
        const current = prev.get(district) ?? baseline.get(district);
        const base = baseline.get(district);
        const merged: PendingChange = {
          selected: change.selected ?? current?.selected ?? true,
          price: change.price ?? current?.price ?? defaultPrice,
          town: change.town ?? prev.get(district)?.town,
        };
        // Back to the saved state -> drop the staged change (only when an explicit row already exists; turning an
        // implicit district into an explicit row at the same values is still a change we should write).
        if (base && !base.implicit && base.selected === merged.selected && base.price === merged.price) {
          next.delete(district);
        } else {
          next.set(district, merged);
        }
        return next;
      });
    },
    [baseline, defaultPrice]
  );

  const revert = useCallback((district: string) => {
    setPending((prev) => {
      const next = new Map(prev);
      next.delete(district);
      return next;
    });
  }, []);

  const handleSelect = useCallback((district: string) => setFocused(district), []);

  // ---- Expand by postcode + radius ---------------------------------------------------------------------------
  const handleExpand = async (e: FormEvent) => {
    e.preventDefault();
    if (!index) return;
    setExpanding(true);
    setExpandError(undefined);
    try {
      const geo = await geocodePostcode(yardPostcode);
      if (!geo) {
        setExpandError("We couldn't find that postcode.");
        return;
      }
      setYardLatLng(geo.latLng);

      const found = districtsWithinRadius(index, geo.latLng, radiusMiles, geo.outcode);
      const existingAreas = new Set(postcodeGroups.map((g) => g.postcode.toUpperCase()));
      const newAreas = Array.from(new Set(found.map((d) => d.area))).filter((a) => !existingAreas.has(a));
      const towns = await lookupDistrictTowns(found.filter((d) => newAreas.includes(d.area)).map((d) => d.district));

      setPending((prev) => {
        const next = new Map(prev);
        const inRadius = new Set(found.map((d) => d.district));

        for (const d of found) {
          const base = baseline.get(d.district);
          if (base?.selected && !prev.has(d.district)) continue; // already covered, leave alone
          next.set(d.district, { selected: true, price: base?.price ?? defaultPrice, town: towns[d.district] });
        }
        // New areas: every other district in the area must be excluded, otherwise the new group covers it.
        for (const area of newAreas) {
          for (const district of Object.keys(index.areas[area]?.districts ?? {})) {
            if (!inRadius.has(district) && !next.has(district)) {
              next.set(district, { selected: false, price: defaultPrice });
            }
          }
        }
        return next;
      });
      setExpandOpen(false);
    } catch (err: any) {
      setExpandError(err?.message ?? "Postcode lookup failed, please try again.");
    } finally {
      setExpanding(false);
    }
  };

  // ---- Save ----------------------------------------------------------------------------------------------------
  const handleSave = () => {
    const payload: IOnboardingCoverageDistrict[] = pendingList.map((p) => ({
      postcode: p.district,
      town: p.town,
      price: p.price,
      selected: p.selected,
    }));

    saveCoverage.mutate(
      {
        yardPostcode: yardLatLng ? yardPostcode : undefined,
        radiusMiles: yardLatLng ? radiusMiles : undefined,
        defaultPrice,
        districts: payload,
      },
      {
        onSuccess: () => {
          saveHandlers.onSuccess();
          setPending(new Map());
        },
        onError: () => saveHandlers.onError(),
      }
    );
  };

  // Only ask for polygons of areas we actually have data for - group postcodes were hand-entered in Barney and
  // may not be clean area codes (and BT / Channel Islands / IoM aren't in the dataset).
  const extraAreas = useMemo(
    () =>
      index
        ? Array.from(new Set(postcodeGroups.map((g) => g.postcode.replace(/\s+/g, "").toUpperCase()))).filter(
            (a) => !!index.areas[a]
          )
        : [],
    [index, postcodeGroups]
  );

  if (indexError) {
    return <div className="rounded-md bg-red-50 p-3 text-sm text-red-700">Couldn't load postcode districts: {indexError}</div>;
  }
  if (!index) {
    return (
      <div className="min-h-[200px]">
        <LoadingWheel />
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
      <CoverageMap
        className="lg:col-span-2 h-[460px] lg:h-[600px]"
        centre={yardLatLng}
        radiusMiles={yardLatLng ? radiusMiles : undefined}
        districts={districts}
        extraAreas={extraAreas}
        focused={focused}
        onSelect={handleSelect}
        legend={{ covered: "Own price", implicit: "Group price", excluded: "Excluded", outside: "Not covered" }}
      />

      <div className="flex h-[460px] flex-col gap-3 lg:h-[600px]">
        {/* Focused district */}
        <div className="rounded-lg border border-gray-200 p-3">
          {!focused ? (
            <p className="text-sm text-gray-500">Click a district on the map to change whether you cover it or what you charge.</p>
          ) : (
            <FocusedDistrict
              district={focused}
              state={focusedState}
              groupName={focusedBaseline?.groupName}
              isPending={pending.has(focused)}
              defaultPrice={defaultPrice}
              onChange={(change) => stage(focused, change)}
              onRevert={() => revert(focused)}
              onClose={() => setFocused(undefined)}
            />
          )}
        </div>

        {/* Expand coverage */}
        <div className="rounded-lg border border-gray-200">
          <button
            type="button"
            className="flex w-full items-center justify-between px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
            onClick={() => setExpandOpen((o) => !o)}
          >
            Add districts around a postcode
            <span className="text-xs text-gray-400">{expandOpen ? "hide" : "show"}</span>
          </button>
          {expandOpen && (
            <form onSubmit={handleExpand} className="space-y-3 border-t border-gray-200 p-3">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium text-gray-600">Postcode</label>
                  <input
                    className={`mt-1 uppercase ${inputClasses}`}
                    value={yardPostcode}
                    onChange={(e) => setYardPostcode(e.target.value)}
                    placeholder="PL1 0LF"
                    required
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600">Radius (miles)</label>
                  <input
                    type="number"
                    min={1}
                    max={200}
                    className={`mt-1 ${inputClasses}`}
                    value={radiusMiles}
                    onChange={(e) => setRadiusMiles(Math.max(1, Math.min(200, +e.target.value || 1)))}
                  />
                </div>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600">Price for newly added districts (£)</label>
                <input
                  type="number"
                  min={0}
                  className={`mt-1 ${inputClasses}`}
                  value={defaultPrice}
                  onChange={(e) => setDefaultPrice(Math.max(0, +e.target.value || 0))}
                />
              </div>
              {expandError && <p className="text-xs text-red-600">{expandError}</p>}
              <ButtonInput isSubmit label={expanding ? "Finding…" : "Add districts"} disabled={expanding || !yardPostcode.trim()} classes="w-full" />
            </form>
          )}
        </div>

        {/* Staged changes */}
        <div className="flex min-h-0 flex-1 flex-col rounded-lg border border-gray-200">
          <div className="flex items-center justify-between border-b border-gray-200 px-3 py-2">
            <span className="text-sm font-medium text-gray-700">
              {pendingList.length === 0
                ? "No unsaved changes"
                : `${pendingList.length} unsaved (${pendingSelected} covered, ${pendingList.length - pendingSelected} excluded)`}
            </span>
            {pendingList.length > 0 && (
              <button type="button" className="text-xs text-gray-500 hover:underline" onClick={() => setPending(new Map())}>
                Discard all
              </button>
            )}
          </div>
          <ul className="min-h-0 flex-1 overflow-y-auto divide-y divide-gray-100">
            {pendingList.map((p) => (
              <li key={p.district}>
                <button
                  type="button"
                  onClick={() => setFocused(p.district)}
                  className={classNames(
                    "flex w-full items-center gap-2 px-3 py-1.5 text-left hover:bg-gray-50",
                    focused === p.district ? "bg-gray-50" : ""
                  )}
                >
                  <span className={classNames("w-12 text-sm font-semibold", p.selected ? "text-gray-900" : "text-red-700 line-through")}>
                    {p.district}
                  </span>
                  <span className="flex-1 truncate text-xs text-gray-500">{p.town ?? ""}</span>
                  <span className="text-xs text-gray-700">{p.selected ? formatGbp(p.price) : "excluded"}</span>
                  <span
                    role="button"
                    title="Undo"
                    onClick={(e) => {
                      e.stopPropagation();
                      revert(p.district);
                    }}
                    className="rounded p-0.5 text-gray-400 hover:bg-gray-200 hover:text-gray-700"
                  >
                    <ArrowUturnLeftIcon className="h-3.5 w-3.5" />
                  </span>
                </button>
              </li>
            ))}
          </ul>
          <div className="border-t border-gray-200 p-3">
            <ButtonInput
              isSubmit={false}
              label={saveCoverage.isLoading ? "Saving…" : `Save ${pendingList.length > 0 ? pendingList.length + " change" + (pendingList.length === 1 ? "" : "s") : "changes"}`}
              onClick={handleSave}
              disabled={pendingList.length === 0 || saveCoverage.isLoading}
              classes="w-full"
            />
          </div>
        </div>
      </div>
    </div>
  );
}

interface FocusedDistrictProps {
  district: string;
  state?: CoverageDistrictState;
  groupName?: string;
  isPending: boolean;
  defaultPrice: number;
  onChange: (change: Partial<PendingChange>) => void;
  onRevert: () => void;
  onClose: () => void;
}

function FocusedDistrict({ district, state, groupName, isPending, defaultPrice, onChange, onRevert, onClose }: FocusedDistrictProps) {
  const covered = state?.selected ?? false;
  const price = state?.price ?? defaultPrice;
  const status = !state
    ? "Not covered — no group for this area yet"
    : !state.selected
    ? "Excluded"
    : state.implicit
    ? `Covered at the ${groupName ?? state.area} group price`
    : "Covered with its own price";

  return (
    <div className="space-y-3">
      <div className="flex items-start justify-between">
        <div>
          <div className="text-lg font-semibold text-gray-900">
            {district}
            {state?.town && <span className="ml-2 text-sm font-normal text-gray-500">{state.town}</span>}
          </div>
          <div className="text-xs text-gray-500">
            {status}
            {isPending && <span className="ml-1 font-medium text-violet-700">· unsaved</span>}
          </div>
        </div>
        <button type="button" onClick={onClose} className="rounded p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-700" title="Close">
          <XMarkIcon className="h-4 w-4" />
        </button>
      </div>

      <div className="flex items-end gap-3">
        <div className="flex-1">
          <label className="block text-xs font-medium text-gray-600">Price (£)</label>
          <div className="relative mt-1">
            <span className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-2 text-xs text-gray-500">£</span>
            <input
              type="number"
              min={0}
              value={price}
              disabled={!covered}
              onChange={(e) => onChange({ price: Math.max(0, +e.target.value || 0), selected: true })}
              className={`pl-5 ${inputClasses} disabled:bg-gray-50 disabled:text-gray-400`}
            />
          </div>
        </div>
        <button
          type="button"
          onClick={() => onChange({ selected: !covered, price })}
          className={classNames(
            "rounded-md px-3 py-2 text-sm font-medium shadow-sm",
            covered
              ? "border border-red-300 bg-white text-red-700 hover:bg-red-50"
              : "bg-primary-600 text-white hover:bg-primary-700"
          )}
        >
          {covered ? "Exclude" : "Cover"}
        </button>
      </div>

      {isPending && (
        <button type="button" onClick={onRevert} className="flex items-center gap-1 text-xs text-gray-500 hover:underline">
          <ArrowUturnLeftIcon className="h-3.5 w-3.5" /> Undo change
        </button>
      )}
    </div>
  );
}
